// Sheets follow the finger (2026-10-23 owner request: feel like an app). On a phone, the bottom sheets (cart, pre-order
// help, register, notify, tiers, review, follow, cancel, Advisor) drag down from their grab area, or from anywhere once
// their content is scrolled to the top; the nav drawer drags back to the left. Let go past a third of the way (at most
// 120px) or with a flick and the sheet slides away and closes, otherwise it springs back. Every other way of closing
// stays as it was. Touch only; nothing happens under reduced motion or for a mouse.
export const SHEETS = '.ns-cart, .ns-tiers-dialog, .ns-register, .ns-follow-dialog, .ns-review-form, .ns-notify, .ns-track-cancel, .ns-help, .ns-advisor-chat';
export const DRAWER = '.ns-drawer';
export const GRAB_PX = 72;
const SKIP = 'input, textarea, select, [contenteditable], [data-ns-no-drag]';
const EASE = 'cubic-bezier(.2, .8, .2, 1)';

// A release closes when the sheet went a third of its size (120px at most) or was flicked faster than .5px/ms.
export const releaseCloses = (distance, size, velocity) => distance > Math.min(120, size / 3) || velocity > 0.5;

// Whether the finger's start leaves room to pull the sheet: no scroller between it and the dialog is scrolled down.
export function atTop(target, dialog) {
  for (let el = target; el; el = el.parentElement) {
    if (el.scrollTop > 0) return false;
    if (el === dialog) return true;
  }
  return true;
}

export function initSheets(doc, view) {
  if (view.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  let drag = null;
  const set = (dialog, offset, size, axis) => {
    dialog.style.transform = offset ? `translate${axis}(${axis === 'X' ? -offset : offset}px)` : '';
    dialog.style.setProperty('--ns-drag', String(Math.min(1, offset / size)));
  };
  const reset = dialog => {
    dialog.style.transform = '';
    dialog.style.removeProperty('--ns-drag');
    dialog.removeAttribute('data-ns-drag');
  };
  doc.addEventListener('touchstart', event => {
    drag = null;
    if (event.touches.length !== 1) return;
    const dialog = event.target.closest?.('dialog[open]');
    if (!dialog || event.target.closest(SKIP)) return;
    const sheet = dialog.matches(SHEETS) && view.matchMedia('(max-width: 749px)').matches;
    if (!sheet && !dialog.matches(DRAWER)) return;
    bind(dialog);
    const box = dialog.getBoundingClientRect();
    const touch = event.touches[0];
    drag = {
      dialog, axis: sheet ? 'Y' : 'X', size: sheet ? box.height : box.width, x: touch.clientX, y: touch.clientY,
      grab: !sheet || touch.clientY - box.top < GRAB_PX || atTop(event.target, dialog), on: false, offset: 0, t: event.timeStamp, v: 0,
    };
  }, { passive: true });
  // The move listener must be able to cancel the scroll, so it is not passive; it sits on the sheets alone, never on the
  // page, so scrolling the page itself never waits on script.
  const bound = new WeakSet();
  const bind = dialog => {
    if (bound.has(dialog)) return;
    bound.add(dialog);
    dialog.addEventListener('touchmove', move, { passive: false });
  };
  const move = event => {
    if (!drag || !drag.grab) return;
    const touch = event.touches[0];
    const dx = touch.clientX - drag.x;
    const dy = touch.clientY - drag.y;
    if (!drag.on) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      // Down for a sheet, left for the drawer, and more that way than across; anything else stays a normal scroll.
      const along = drag.axis === 'Y' ? dy > 0 && dy > Math.abs(dx) : dx < 0 && -dx > Math.abs(dy);
      if (!along) { drag = null; return; }
      drag.on = true;
      drag.dialog.setAttribute('data-ns-drag', '');
    }
    event.preventDefault();
    const offset = Math.max(0, drag.axis === 'Y' ? dy : -dx);
    drag.v = (offset - drag.offset) / Math.max(1, event.timeStamp - drag.t);
    drag.offset = offset;
    drag.t = event.timeStamp;
    set(drag.dialog, offset, drag.size, drag.axis);
  };
  for (const dialog of doc.querySelectorAll(`dialog:is(${SHEETS}, ${DRAWER})`)) bind(dialog);
  const end = () => {
    const current = drag;
    drag = null;
    if (!current?.on) return;
    const { dialog, offset, size, axis, v } = current;
    const closes = releaseCloses(offset, size, v);
    const to = closes ? size : 0;
    const from = `translate${axis}(${axis === 'X' ? -offset : offset}px)`;
    const target = `translate${axis}(${axis === 'X' ? -to : to}px)`;
    const finish = () => {
      if (closes) dialog.close();
      reset(dialog);
    };
    if (typeof dialog.animate !== 'function') return finish();
    dialog.style.setProperty('--ns-drag', closes ? '1' : '0');
    dialog.animate([{ transform: from }, { transform: target }], { duration: closes ? 200 : 260, easing: EASE, fill: 'forwards' }).finished.then(animation => {
      finish();
      animation?.cancel?.();
    }, finish);
  };
  doc.addEventListener('touchend', end);
  doc.addEventListener('touchcancel', end);
  return true;
}

if (typeof document !== 'undefined') initSheets(document, window);
